import {
  TEntityResponse,
  TEntityListResponse,
  TUpdateDeviceTypeReqBody,
  TDeviceTypeResBody,
  TDeviceTypeSummaryResBody,
  TDeviceSummaryResBody,
  TDeviceProfileSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TDeviceTypesClient = {
  update(
    id: string,
    body: TUpdateDeviceTypeReqBody
  ): Promise<TEntityResponse<'device-type', TDeviceTypeResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'device-type', TDeviceTypeResBody> | undefined>;
  list(limit: number, page: number): Promise<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody> | undefined>;
  list_area_devices(areaId: string): Promise<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody> | undefined>;
  list_device_type_devices(
    limit: number,
    page: number,
    deviceTypeId: string,
    filter?: string,
    sort?: string
  ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_all_devices_by_type_devices(
    limit: number,
    page: number,
    deviceTypeId: string,
    sort?: string
  ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  list_device_type_device_profiles(
    deviceTypeId: string
  ): Promise<TEntityListResponse<'device-profile', TDeviceProfileSummaryResBody> | undefined>;
};

function DeviceTypesClient(apiClient: TAPIClient): TDeviceTypesClient {
  return {
    async update(
      id: string,
      body: TUpdateDeviceTypeReqBody
    ): Promise<TEntityResponse<'device-type', TDeviceTypeResBody> | undefined> {
      const data = await apiClient.put<TUpdateDeviceTypeReqBody, TEntityResponse<'device-type', TDeviceTypeResBody>>(
        `/device-types/${id}`,
        body
      );

      if (data && data.type !== 'device-type') {
        throw new Error('Invalid entity type received, expected: device-type, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/device-types/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'device-type', TDeviceTypeResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'device-type', TDeviceTypeResBody>>(`/device-types/${id}`);

      if (data && data.type !== 'device-type') {
        throw new Error('Invalid entity type received, expected: device-type, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody>>(
        `/device-types?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'device-type') {
        throw new Error('Invalid entity type received, expected: device-type, received: ' + data.type);
      }

      return data;
    },

    async list_area_devices(
      areaId: string
    ): Promise<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-type', TDeviceTypeSummaryResBody>>(
        `/areas/${areaId}/devices`
      );

      if (data && data.type !== 'device-type') {
        throw new Error('Invalid entity type received, expected: device-type, received: ' + data.type);
      }

      return data;
    },

    async list_device_type_devices(
      limit: number,
      page: number,
      deviceTypeId: string,
      filter?: string,
      sort?: string
    ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      let data;
      if (filter) {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/device-types/${deviceTypeId}/devices?limit=${limit}&page=${page}&${filter}${sort}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
          `/device-types/${deviceTypeId}/devices?limit=${limit}&page=${page}${sort}`
        );
      }

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_all_devices_by_type_devices(
      limit: number,
      page: number,
      deviceTypeId: string,
      sort?: string
    ): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
        `/device-types/${deviceTypeId}/devices?limit=${limit}&page=${page}${sort}`
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async list_device_type_device_profiles(
      deviceTypeId: string
    ): Promise<TEntityListResponse<'device-profile', TDeviceProfileSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device-profile', TDeviceProfileSummaryResBody>>(
        `/device-types/${deviceTypeId}/device-profiles`
      );

      if (data && data.type !== 'device-profile') {
        throw new Error('Invalid entity type received, expected: device-profile, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TDeviceTypesClient, DeviceTypesClient };
